import { KeyRound, Search, X } from 'lucide-react'
import { zodResolver } from '@hookform/resolvers/zod'
import { useCallback, useEffect, useMemo, useState } from 'react'
import { useForm } from 'react-hook-form'
import { z } from 'zod'
import { passwordResetService } from '../api/services/password-reset.service'
import { PermissionGate } from '../components/auth/PermissionGate'
import { AppShell } from '../components/layout/AppShell'
import { Button } from '../components/ui/button'
import { FieldLabel, FormRequiredLegend } from '../components/ui/field-label'
import { Input } from '../components/ui/input'
import { ListPagination } from '../components/ui/list-pagination'
import { PasswordInput } from '../components/ui/password-input'
import { DEFAULT_PAGE_SIZE, resolveListPage, type PaginationMeta } from '../lib/api-list'
import { IconActionButton, TableIconActions } from '../components/ui/table-icon-actions'
import { useAuth } from '../context/AuthContext'
import { PASSWORD_HINT, setPasswordFormSchema } from '../lib/password'
import type { PasswordResetRequest } from '../types/password-reset'

const setPasswordSchema = setPasswordFormSchema

type SetPasswordFormValues = z.infer<typeof setPasswordSchema>

export function PasswordManagementPage() {
  const { can } = useAuth()
  const [requests, setRequests] = useState<PasswordResetRequest[]>([])
  const [hasLoaded, setHasLoaded] = useState(false)
  const [searchInput, setSearchInput] = useState('')
  const [search, setSearch] = useState('')
  const [target, setTarget] = useState<PasswordResetRequest | null>(null)
  const [submitting, setSubmitting] = useState(false)
  const [page, setPage] = useState(1)
  const [perPage, setPerPage] = useState<number>(DEFAULT_PAGE_SIZE)
  const [meta, setMeta] = useState<PaginationMeta | null>(null)

  const form = useForm<SetPasswordFormValues>({
    resolver: zodResolver(setPasswordSchema),
    defaultValues: { password: '', password_confirmation: '' },
  })

  const activeFilterCount = useMemo(() => (search.trim() ? 1 : 0), [search])

  const clearFilters = () => {
    setSearch('')
    setSearchInput('')
  }

  const loadRequests = useCallback(async () => {
    try {
      const response = await passwordResetService.list({
        page,
        per_page: perPage,
        search: search.trim() || undefined,
      })
      const { items, meta: nextMeta } = resolveListPage(response, page, perPage)
      setRequests(items)
      setMeta(nextMeta)
    } catch {
      setRequests([])
      setMeta(null)
    } finally {
      setHasLoaded(true)
    }
  }, [page, perPage, search])

  useEffect(() => {
    void loadRequests()
  }, [loadRequests])

  useEffect(() => {
    setPage(1)
  }, [search])

  const openSetPassword = (request: PasswordResetRequest) => {
    setTarget(request)
    form.reset({ password: '', password_confirmation: '' })
  }

  const closeSetPassword = () => {
    setTarget(null)
    form.reset({ password: '', password_confirmation: '' })
  }

  const onSubmit = form.handleSubmit(async (values) => {
    if (!target) {
      return
    }

    setSubmitting(true)

    try {
      await passwordResetService.updateUserPassword({
        reset_password_request_id: target.id,
        password: values.password,
        password_confirmation: values.password_confirmation,
      })
      closeSetPassword()
      await loadRequests()
    } catch {
      // toasted globally
    } finally {
      setSubmitting(false)
    }
  })

  const displayName = (request: PasswordResetRequest) => {
    const parts = [request.first_name, request.surname].filter(Boolean)
    return parts.length > 0 ? parts.join(' ') : request.email ?? 'Unknown user'
  }

  return (
    <AppShell>
      <div className="mx-auto max-w-6xl px-2 py-2">
        <div className="mb-6">
          <h1 className="font-display text-2xl font-bold">Password requests</h1>
          <p className="mt-1 text-sm text-ink-600">
            Users who cannot sign in can request a reset from the login page. Set a new
            password here — it is not emailed automatically; share it with the user securely.
          </p>
        </div>

        <section className="mb-6 rounded-xl border border-parch-100 bg-white p-4 shadow-card sm:p-5">
          <div className="flex flex-wrap items-center justify-between gap-2">
            <div className="flex items-center gap-2">
              <h2 className="text-sm font-semibold text-ink-900">Filters</h2>
              {activeFilterCount > 0 && (
                <span className="inline-flex h-5 min-w-[20px] items-center justify-center rounded-full bg-green-100 px-1.5 text-xs font-semibold text-green-700">
                  {activeFilterCount}
                </span>
              )}
            </div>
          </div>

          <div className="mt-4 flex flex-wrap items-stretch gap-2">
            <div className="relative min-w-[12rem] flex-1">
              <Search className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-ink-400" />
              <Input
                className="pl-9"
                placeholder="Search name, email…"
                value={searchInput}
                onChange={(event) => setSearchInput(event.target.value)}
                onKeyDown={(event) => {
                  if (event.key === 'Enter') {
                    setSearch(searchInput.trim())
                  }
                }}
              />
            </div>
            <Button onClick={() => setSearch(searchInput.trim())}>Search</Button>
            {activeFilterCount > 0 && (
              <Button variant="secondary" onClick={clearFilters}>
                <X className="h-4 w-4" />
                Clear
              </Button>
            )}
          </div>
        </section>

        <section className="overflow-hidden rounded-lg border border-parch-100 bg-white shadow-card">
          <div className="overflow-x-auto">
            <table className="min-w-full text-left text-sm">
              <thead className="border-b border-parch-100 bg-parch-50 text-ink-600">
                <tr>
                  <th className="px-4 py-3 font-medium">Name</th>
                  <th className="px-4 py-3 font-medium">Email</th>
                  <th className="px-4 py-3 font-medium">Requested</th>
                  <th className="px-4 py-3 font-medium">Actions</th>
                </tr>
              </thead>
              <tbody>
                {hasLoaded && requests.length === 0 ? (
                  <tr>
                    <td colSpan={5} className="px-4 py-6 text-sm text-ink-500">
                      No password reset requests.
                    </td>
                  </tr>
                ) : (
                  requests.map((request) => (
                    <tr key={request.id} className="border-b border-parch-50 last:border-0">
                      <td className="px-4 py-3">{displayName(request)}</td>
                      <td className="px-4 py-3">{request.email ?? '—'}</td>
                      <td className="px-4 py-3">
                        {request.created_at
                          ? new Date(request.created_at).toLocaleString()
                          : '—'}
                      </td>
                      <td className="px-4 py-3">
                        <PermissionGate permission="reset-password-update">
                          <TableIconActions>
                            <IconActionButton
                              icon={KeyRound}
                              label="Set Password"
                              onClick={() => openSetPassword(request)}
                            />
                          </TableIconActions>
                        </PermissionGate>
                        {!can('reset-password-update') && (
                          <span className="text-xs text-ink-400">No update permission</span>
                        )}
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>
        </section>

        <ListPagination
          meta={meta}
          perPage={perPage}
          onPageChange={setPage}
          onPerPageChange={(n) => {
            setPerPage(n)
            setPage(1)
          }}
        />

        {target && (
          <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4">
            <div className="w-full max-w-md rounded-lg bg-white p-6 shadow-card">
              <h2 className="font-display text-xl font-bold">Set new password</h2>
              <p className="mt-1 text-sm text-ink-600">
                For <span className="font-medium text-ink-800">{displayName(target)}</span>
                {target.email ? ` (${target.email})` : ''}. No email is sent by the system.
              </p>

              <form className="mt-6 space-y-4" onSubmit={onSubmit}>
                <FormRequiredLegend />
                <div>
                  <FieldLabel required>New password</FieldLabel>
                  <p className="mt-1 text-xs text-ink-500">{PASSWORD_HINT}</p>
                  <PasswordInput className="mt-1" autoComplete="new-password" {...form.register('password')} />
                  {form.formState.errors.password && (
                    <p className="mt-1 text-xs text-danger">{form.formState.errors.password.message}</p>
                  )}
                </div>
                <div>
                  <FieldLabel required>Confirm password</FieldLabel>
                  <PasswordInput
                    className="mt-1"
                    autoComplete="new-password"
                    {...form.register('password_confirmation')}
                  />
                  {form.formState.errors.password_confirmation && (
                    <p className="mt-1 text-xs text-danger">
                      {form.formState.errors.password_confirmation.message}
                    </p>
                  )}
                </div>
                <div className="flex justify-end gap-2 pt-2">
                  <Button type="button" variant="secondary" onClick={closeSetPassword}>
                    Cancel
                  </Button>
                  <Button type="submit" disabled={submitting}>
                    {submitting ? 'Saving…' : 'Update Password'}
                  </Button>
                </div>
              </form>
            </div>
          </div>
        )}
      </div>
    </AppShell>
  )
}
